<template>
  <ReportShell
    v-model="dialog"
    title="Watchlist Screening Report"
    subtitle="Confidential — For Compliance Use Only"
    :header-name="toTitleCase((result.names || names).join(', '))"
    :ref-label="meta.date + ' ' + meta.time"
    @print="printReport"
  >
    <ReportStatGrid :stats="headlineStats" />

    <ReportSection title="Search Parameters">
      <table class="doc-table">
        <tbody>
          <tr>
            <td class="doc-label">Names searched</td>
            <td>{{ toTitleCase((result.names || names).join(', ')) }}</td>
          </tr>
          <tr v-if="account">
            <td class="doc-label">Account holder</td>
            <td>{{ toTitleCase(account.personName) }} &mdash; {{ account.mainAccountNumber }}</td>
          </tr>
          <tr>
            <td class="doc-label">Search type</td>
            <td>{{ result.adHoc ? 'Ad-hoc (no account linked)' : 'Account-linked' }}</td>
          </tr>
          <tr>
            <td class="doc-label">Providers searched</td>
            <td>{{ result.providers.map(p => providerLabel(p.provider)).join(', ') }}</td>
          </tr>
          <tr v-if="options.minimum_score">
            <td class="doc-label">Minimum score</td>
            <td>{{ options.minimum_score }}</td>
          </tr>
          <tr v-if="options.data_sets && options.data_sets.length">
            <td class="doc-label">Data sets</td>
            <td>{{ options.data_sets.join(', ') }}</td>
          </tr>
          <tr>
            <td class="doc-label">Fuzzy matching</td>
            <td>{{ options.use_fuzzy ? 'Enabled' : 'Disabled' }}</td>
          </tr>
          <tr>
            <td class="doc-label">Alias search</td>
            <td>{{ options.search_alias ? 'Enabled' : 'Disabled' }}</td>
          </tr>
          <tr>
            <td class="doc-label">Performed by</td>
            <td>{{ toTitleCase(meta.officer) }}</td>
          </tr>
        </tbody>
      </table>
    </ReportSection>

    <ReportSection title="Summary">
      <table class="doc-table">
        <thead><tr><th>Provider</th><th>List date</th><th>Status</th><th style="text-align:right">Results returned</th></tr></thead>
        <tbody>
          <tr v-for="p in result.providers" :key="'sum-' + p.provider">
            <td>{{ providerLabel(p.provider) }}</td>
            <td>{{ listDateText(p) }}</td>
            <td><span class="doc-pill" :style="watchlistPillStyle(p.matchStatus)">{{ providerStatusLabel(p.matchStatus) }}</span></td>
            <td class="doc-num" style="text-align:right">{{ p.matchCount || 0 }}</td>
          </tr>
        </tbody>
      </table>
    </ReportSection>

    <!-- One numbered section per provider that actually returned hits -->
    <ReportSection
      v-for="p in hitProviders"
      :key="'hits-' + p.provider"
      :title="providerLabel(p.provider) + ' — ' + p.matchCount + ' Result' + (p.matchCount !== 1 ? 's' : '')"
      badge="Possible match"
      badge-tone="danger"
    >
      <div v-for="(hit, hi) in p.matches" :key="p.provider + '-' + hi" class="doc-subblock">
        <div class="doc-subblock-header">
          <span class="doc-subblock-title">{{ hi + 1 }}. {{ toTitleCase(hit.name) }}</span>
          <span class="doc-pill" style="background:#FCEBEB;color:#791F1F">Score: {{ hit.score }}<span v-if="hit.max_score"> / {{ hit.max_score }}</span></span>
        </div>
        <div class="doc-subblock-body">
          <table class="doc-table doc-table--tight">
            <tbody>
              <tr v-if="hit.aliases"><td class="doc-label">Aliases</td><td>{{ toTitleCase(hit.aliases) }}</td></tr>
              <tr v-if="hit.address"><td class="doc-label">Address</td><td>{{ toTitleCase(hit.address) }}</td></tr>
              <tr><td class="doc-label">Source</td><td>{{ toTitleCase(hit.source_name) }} ({{ hit.source_code }})</td></tr>
              <tr v-if="isSafeUrl(hit.source_url)"><td class="doc-label">Source link</td><td><a :href="hit.source_url" target="_blank" rel="noopener noreferrer" style="word-break:break-all">{{ hit.source_url }}</a></td></tr>
              <tr><td class="doc-label">Type</td><td>{{ toTitleCase(hit.type) }}</td></tr>
              <tr><td class="doc-label">Data set</td><td>{{ toTitleCase(hit.data_set) }}</td></tr>
              <tr v-if="hit.last_updated"><td class="doc-label">List date</td><td>{{ describeListDate(hit.last_updated, hit.date_method) }}</td></tr>
              <tr v-for="(val, key) in flatMetaFields(hit.meta_data)" :key="key">
                <td class="doc-label">{{ formatMetaKey(key) }}</td>
                <td>{{ toTitleCaseSmart(key, val) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ReportSection>

  </ReportShell>
</template>

<script>

import ReportShell from '../../../../shared/reports-kit/components/ReportShell.vue';
import ReportSection from '../../../../shared/reports-kit/components/ReportSection.vue';
import ReportStatGrid from '../../../../shared/reports-kit/components/ReportStatGrid.vue';
import { toRoman } from '../../../../shared/reports-kit/utils/roman';
import reportFormatMixin from '../../../../shared/reports-kit/mixins/reportFormat.mixin';
import reportPrintMixin from '../../../../shared/reports-kit/mixins/reportPrint.mixin';
import {
  HIDDEN_META_KEYS, providerLabel, describeListDate, isSafeUrl,
} from '@/modules/amlpro/services/watchlist-sources';

const NO_TITLECASE_KEYS = [
  'id', 'data_id', 'ncic', 'publication', 'last_updated', 'date_effective',
  'expiration_date', 'fr_citation', 'standard_order', 'status', 'path',
  'place_of_birth', 'date_of_birth',
];

export default {
  name: 'WatchlistPrintReport',

  components: { ReportShell, ReportSection, ReportStatGrid },

  mixins: [reportFormatMixin, reportPrintMixin],

  props: {
    value:   { type: Boolean, default: false },
    result:  { type: Object,  required: true },
    meta:    { type: Object,  required: true },
    account: { type: Object,  default: null  },
    options: { type: Object,  required: true },
    names:   { type: Array,   default: () => [] },
  },

  data: () => ({ dialog: false }),

  watch: {
    value(val)  { this.dialog = val; },
    dialog(val) { this.$emit('input', val); },
  },

  computed: {
    totalResults() {
      if (!this.result || !this.result.providers) return 0;
      return this.result.providers.reduce((sum, p) => sum + (p.matchCount ?? 0), 0);
    },
    hitProviders() {
      return (this.result.providers || []).filter(p => p.matches && p.matches.length);
    },
    headlineStats() {
      return [
        { label: 'Providers searched', value: this.result.providers.length, tone: 'neutral' },
        { label: 'Possible matches', value: this.hitProviders.length, tone: this.hitProviders.length ? 'danger' : 'success' },
        { label: 'Total results', value: this.totalResults, tone: 'neutral' },
      ];
    },
  },

  methods: {
    providerLabel, describeListDate, isSafeUrl,

    listDateText(p) {
      if (!p || !p.list || !p.list.list_updated_at) return '—';
      return describeListDate(p.list.list_updated_at, p.list.date_method);
    },

    providerStatusLabel(status) {
      if (status === 'POSSIBLE_MATCH') return 'Possible match';
      if (status === 'ERROR') return 'Error';
      return 'No match';
    },

    toTitleCaseSmart(key, val) {
      if (typeof val !== 'string') return val;
      if (NO_TITLECASE_KEYS.includes(key)) return val;
      if (val.length <= 3 && !val.includes(' ')) return val;
      if (val.startsWith('http')) return val;
      const nameKeys = ['name', 'first_name', 'last_name', 'nationality',
                        'place_of_birth', 'constituency', 'description',
                        'position', 'party', 'caution', 'remarks', 'action'];
      if (nameKeys.some(k => key.toLowerCase().includes(k))) return this.toTitleCase(val);
      if (val === val.toUpperCase() && /[A-Z]/.test(val)) return this.toTitleCase(val);
      return val;
    },

    flatMetaFields(meta) {
      if (!meta || typeof meta !== 'object') return {};
      const skip = [
        'aliases', 'addresses', 'vessel_info', 'images', 'files',
        'field_offices', 'locations', 'names', 'possible_countries',
        'possible_states', 'program_list', 'occupations', ...HIDDEN_META_KEYS,
      ];
      const out = {};
      for (const [k, v] of Object.entries(meta)) {
        if (skip.includes(k)) continue;
        if (v === null || v === undefined || v === '') continue;
        if (typeof v === 'object') continue;
        out[k] = v;
      }
      return out;
    },

    formatMetaKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },

    printReport() {
      const e = this.escapeHtml.bind(this);
      const r = this.result;

      const searchedNames = this.toTitleCase((r.names || this.names).join(', '));

      const hitSectionsHtml = this.hitProviders.map((p, i) => this.buildSectionHtml({
        number: toRoman(3 + i), // I = Search Parameters, II = Summary, III.. = per-provider hits
        title: `${this.providerLabel(p.provider)} — ${p.matchCount} Result${p.matchCount !== 1 ? 's' : ''}`,
        badgeHtml: `<span class="pill" style="background:#FCEBEB;color:#791F1F">Possible match</span>`,
        bodyHtml: p.matches.map((hit, hi) => this.buildSubblockHtml({
          title: `${hi + 1}. ${this.toTitleCase(hit.name)}`,
          badgeHtml: `<span class="pill" style="background:#FCEBEB;color:#791F1F">Score: ${e(hit.score)}${hit.max_score ? ' / ' + e(hit.max_score) : ''}</span>`,
          bodyHtml: `
            <table class="tight"><tbody>
              ${hit.aliases ? `<tr><td class="label">Aliases</td><td>${e(this.toTitleCase(hit.aliases))}</td></tr>` : ''}
              ${hit.address ? `<tr><td class="label">Address</td><td>${e(this.toTitleCase(hit.address))}</td></tr>` : ''}
              <tr><td class="label">Source</td><td>${e(this.toTitleCase(hit.source_name))} (${e(hit.source_code)})</td></tr>
              ${this.isSafeUrl(hit.source_url) ? `<tr><td class="label">Source link</td><td style="word-break:break-all">${e(hit.source_url)}</td></tr>` : ''}
              <tr><td class="label">Type</td><td>${e(this.toTitleCase(hit.type))}</td></tr>
              <tr><td class="label">Data set</td><td>${e(this.toTitleCase(hit.data_set))}</td></tr>
              ${hit.last_updated ? `<tr><td class="label">List date</td><td>${e(this.describeListDate(hit.last_updated, hit.date_method))}</td></tr>` : ''}
              ${Object.entries(this.flatMetaFields(hit.meta_data)).map(([key, val]) => `
                <tr><td class="label">${e(this.formatMetaKey(key))}</td><td>${e(this.toTitleCaseSmart(key, val))}</td></tr>
              `).join('')}
            </tbody></table>`,
        })).join(''),
      })).join('');

      const bodyHtml = `
        ${this.buildPrintLetterheadHtml({
          title: 'Watchlist Screening Report',
          subtitle: 'Confidential — For Compliance Use Only',
          refLabel: `${e(this.meta.date)} ${e(this.meta.time)}`,
        })}

        ${this.buildStatGridHtml(this.headlineStats)}

        ${this.buildSectionHtml({
          number: 'I',
          title: 'Search Parameters',
          bodyHtml: `
            <table><tbody>
              <tr><td class="label">Names searched</td><td>${e(searchedNames)}</td></tr>
              ${this.account ? `<tr><td class="label">Account holder</td><td>${e(this.toTitleCase(this.account.personName))} &mdash; ${e(this.account.mainAccountNumber)}</td></tr>` : ''}
              <tr><td class="label">Search type</td><td>${r.adHoc ? 'Ad-hoc (no account linked)' : 'Account-linked'}</td></tr>
              <tr><td class="label">Providers searched</td><td>${e(r.providers.map(p => this.providerLabel(p.provider)).join(', '))}</td></tr>
              ${this.options.minimum_score ? `<tr><td class="label">Minimum score</td><td>${e(this.options.minimum_score)}</td></tr>` : ''}
              ${this.options.data_sets && this.options.data_sets.length ? `<tr><td class="label">Data sets</td><td>${e(this.options.data_sets.join(', '))}</td></tr>` : ''}
              <tr><td class="label">Fuzzy matching</td><td>${this.options.use_fuzzy ? 'Enabled' : 'Disabled'}</td></tr>
              <tr><td class="label">Alias search</td><td>${this.options.search_alias ? 'Enabled' : 'Disabled'}</td></tr>
              <tr><td class="label">Performed by</td><td>${e(this.toTitleCase(this.meta.officer))}</td></tr>
            </tbody></table>`,
        })}

        ${this.buildSectionHtml({
          number: 'II',
          title: 'Summary',
          bodyHtml: `
            <table>
              <thead><tr><th>Provider</th><th>List date</th><th>Status</th><th style="text-align:right">Results returned</th></tr></thead>
              <tbody>
                ${r.providers.map(p => `
                  <tr>
                    <td>${e(this.providerLabel(p.provider))}</td>
                    <td>${e(this.listDateText(p))}</td>
                    <td><span class="pill" style="${this.watchlistPillStyle(p.matchStatus)}">${e(this.providerStatusLabel(p.matchStatus))}</span></td>
                    <td class="num" style="text-align:right">${e(p.matchCount || 0)}</td>
                  </tr>`).join('')}
              </tbody>
            </table>`,
        })}

        ${hitSectionsHtml}

        ${this.buildPrintFooterHtml()}
      `;

      this.printHtml({ title: `Watchlist Screening Report - ${searchedNames}`, bodyHtml });
    },
  },
};
</script>